import { z } from 'zod'
import { oc } from '@orpc/contract'
import { billingCaseDtoSchema } from './update-case.contract'
import { caseDetailPaymentSchema } from './case-detail.contract'

// Manual insurance-payment entry (accounting-plan Task 13, reworked by Task 26): billers
// record payments received outside the system (ACH/EFT, paper check, or a virtual card
// charged in the org's Stripe Dashboard — the Task 21 motoUnavailable fallback). Each
// payment carries ITS OWN EOB figures — allowed + patient responsibility — and the
// recorded amount defaults to allowed − patient_resp and may be entered lower (payer fees). The
// case's allowed/patient_resp become recomputed totals. Posting validation: the payment's
// allowed must fit the claim's remaining charge (provider charge − Σ already paid) unless
// explicitly confirmed (409 otherwise, warning when confirmed).
// Method-conditional required fields: ach_eft needs the EFT trace / remittance ref,
// check needs the check number and date, vcc needs the Stripe payment id as the
// external ref. checkDate is journaled only — billing_payment has no column for it.
export const recordPaymentInputSchema = z
  .object({
    encounterId: z.string(),
    method: z.enum(['ach_eft', 'check', 'vcc']),
    /** Allowed amount from THIS payment's EOB/remittance. */
    allowedCents: z.number().int().min(0),
    /** Patient responsibility from THIS payment's EOB/remittance. */
    patientRespCents: z.number().int().min(0),
    /**
     * Optional split of the patient responsibility (docs/GLOSSARY.md "Billing"). Each non-zero
     * editable field becomes/updates a draft Invoice of its kind; nothing is written to the
     * ledger. Fields for kinds already sent/collected/refunded must be left empty.
     */
    deductibleCents: z.number().int().min(0).optional(),
    copayCents: z.number().int().min(0).optional(),
    coinsuranceCents: z.number().int().min(0).optional(),
    /**
     * The insurance payment actually received (check / EFT amount). Omitted → allowed −
     * patientResp (the previous behaviour). Never above that difference: the shortfall is
     * the payer's fees, derived everywhere it is shown, never stored.
     */
    amountCents: z.number().int().positive().optional(),
    payerName: z.string().min(1),
    /** Calendar date (YYYY-MM-DD), from the EOB / check. */
    paymentDate: z.iso.date(),
    depositDate: z.iso.date().optional(),
    /** EFT trace / remittance reference (ach_eft) or Stripe payment id (vcc): required. */
    externalRef: z.string().min(1).optional(),
    /** check: required. */
    checkNumber: z.string().min(1).optional(),
    /** check: required; journal-only. */
    checkDate: z.iso.date().optional(),
    accountLast4: z.string().optional(),
    /** DocumentReference id of the EOB PDF (upload lands in Task 14). */
    eobDocumentId: z.string().optional(),
    /** Explicit confirmation when the payment exceeds the expected insurance amount. */
    confirmOverpayment: z.boolean().optional(),
  })
  .superRefine((value, ctx) => {
    const expectedCents = value.allowedCents - value.patientRespCents
    if (expectedCents <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['allowedCents'],
        message: 'The derived payment amount (allowed − patient responsibility) must be positive',
      })
    } else if (value.amountCents != null && value.amountCents > expectedCents) {
      ctx.addIssue({
        code: 'custom',
        path: ['amountCents'],
        message: 'The insurance payment cannot exceed allowed − patient responsibility',
      })
    }
    const breakdownCents =
      (value.deductibleCents ?? 0) + (value.copayCents ?? 0) + (value.coinsuranceCents ?? 0)
    if (breakdownCents > value.patientRespCents) {
      ctx.addIssue({
        code: 'custom',
        path: ['patientRespCents'],
        message: 'The patient responsibility breakdown exceeds the patient responsibility',
      })
    }
    if ((value.method === 'ach_eft' || value.method === 'vcc') && !value.externalRef) {
      ctx.addIssue({
        code: 'custom',
        path: ['externalRef'],
        message: `externalRef is required for ${value.method} payments`,
      })
    }
    if (value.method === 'check') {
      if (!value.checkNumber) {
        ctx.addIssue({
          code: 'custom',
          path: ['checkNumber'],
          message: 'checkNumber is required for check payments',
        })
      }
      if (!value.checkDate) {
        ctx.addIssue({
          code: 'custom',
          path: ['checkDate'],
          message: 'checkDate is required for check payments',
        })
      }
    }
  })

export const billingV2RecordPayment = oc
  .route({ method: 'POST', path: '/v2/billing/payment/record' })
  .input(recordPaymentInputSchema)
  .output(
    z.object({
      payment: caseDetailPaymentSchema,
      case: billingCaseDtoSchema,
      /** Non-blocking notices (e.g. a confirmed overpayment). */
      warnings: z.array(z.string()),
    }),
  )
